import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';
import FAQ from '../home/components/FAQ';
import FinalCTA from '../home/components/FinalCTA';
import ServiceAreas from '../home/components/ServiceAreas';
import TrustBar from '../home/components/TrustBar';
import AIAnswerBlock from '../home/components/AIAnswerBlock';
import MidPageCTA from '../home/components/MidPageCTA';

const services = [
  {
    id: 'corporate-video',
    icon: 'ri-movie-2-fill',
    title: 'Corporate Video Production',
    headline: 'Broadcast-Grade Content That Converts',
    desc: 'From 30-second broadcast spots to full campaign series, our corporate video production team creates content that captures attention, communicates your value proposition, and drives measurable action. Every frame is executed at broadcast television standards.',
    deliverables: ['Broadcast TV Commercials', 'Digital Ad Campaigns', 'Product Showcase Videos', 'Corporate Promos & Reels', 'Social Media Campaign Content'],
    img: 'https://public.readdy.ai/ai/img_res/c05dce663e74e01be9ba03db6e1295bd.jpg',
    keyword: 'Corporate Video Production',
  },
  {
    id: 'brand-story',
    icon: 'ri-film-fill',
    title: 'Brand Story Films',
    headline: 'Your Story Is Your Strongest Asset',
    desc: 'Cinematic brand documentaries that humanize your organization, communicate your mission with emotional power, and forge lasting connections with audiences who matter most to your growth. Strategy meets storytelling in every production.',
    deliverables: ['Founder & Origin Stories', 'Mission-Driven Documentaries', 'Team & Culture Films', 'Investor & Pitch Videos', 'Annual Report Films'],
    img: 'https://readdy.ai/api/search-image?query=beautiful%20cinematic%20brand%20documentary%20film%20production%20interview%20setup%20elegant%20subject%20in%20director%20chair%20warm%20bokeh%20background%20soft%20key%20light%20dark%20studio%20rich%20background%20high%20end%20storytelling%20authentic%20emotional%20depth%20professional&width=1600&height=800&seq=svp3_02&orientation=landscape',
    keyword: 'Brand Story Films',
  },
  {
    id: 'live-streaming',
    icon: 'ri-broadcast-fill',
    title: 'Live Streaming Services',
    headline: 'Broadcast Live. Worldwide.',
    desc: 'Professional live streaming services for events, church services, conferences, and brand activations. Multi-camera, broadcast-grade real-time production streamed directly to YouTube, Facebook, custom platforms, or any destination globally.',
    deliverables: ['Multi-Camera Live Events', 'YouTube & Facebook Live', 'Virtual Conferences', 'Hybrid Event Streaming', 'Church & Service Streaming'],
    img: 'https://readdy.ai/api/search-image?query=professional%20broadcast%20live%20streaming%20production%20control%20room%20multiple%20large%20screens%20video%20switcher%20waveform%20monitors%20dark%20technical%20atmosphere%20teal%20blue%20glow%20professional%20television%20broadcast%20operators%20camera%20operators%20high%20end%20equipment&width=1600&height=800&seq=svp3_03&orientation=landscape',
    keyword: 'Live Streaming Services',
  },
  {
    id: 'event-coverage',
    icon: 'ri-live-fill',
    title: 'Event Coverage',
    headline: 'Never Miss a Defining Moment',
    desc: 'Multi-camera event production that captures the energy, emotion, and defining moments of your most important gatherings — delivered in broadcast quality for maximum replay value and audience engagement across platforms.',
    deliverables: ['Corporate Conferences', 'Gala & Award Ceremonies', 'Product Launch Events', 'Church & Religious Events', 'Community & Cultural Events'],
    img: 'https://readdy.ai/api/search-image?query=massive%20professional%20event%20production%20stage%20dramatic%20beam%20lights%20crowd%20energy%20spectacular%20venue%20dark%20atmosphere%20professional%20broadcast%20multi%20camera%20coverage%20cinematic%20high%20contrast%20premium%20stage%20production&width=1600&height=800&seq=svp3_04&orientation=landscape',
    keyword: 'Event Video Coverage',
  },
  {
    id: 'photography',
    icon: 'ri-camera-3-fill',
    title: 'Photography Services',
    headline: 'Images That Stop the Scroll',
    desc: 'Commercial photography, executive headshots, and editorial imagery crafted to represent your brand with authority, precision, and visual impact across every marketing platform, campaign, and publication.',
    deliverables: ['Commercial Photography', 'Executive Headshots', 'Product Photography', 'Editorial & Lifestyle', 'Event Photography'],
    img: 'https://readdy.ai/api/search-image?query=luxury%20commercial%20photography%20studio%20professional%20strobe%20lighting%20dramatic%20directional%20light%20dark%20rich%20background%20high%20end%20editorial%20brand%20photography%20beautiful%20light%20shaping%20moody%20cinematic%20atmosphere%20premium%20corporate%20photography&width=1600&height=800&seq=svp3_05&orientation=landscape',
    keyword: 'Commercial Photography',
  },
  {
    id: 'creative-editing',
    icon: 'ri-scissors-2-fill',
    title: 'Creative Direction & Editing',
    headline: 'Where Raw Footage Becomes Legacy',
    desc: 'Post-production excellence — world-class color grading, professional sound design, motion graphics, and editorial strategy that transforms raw footage into a polished cinematic asset ready to perform and convert.',
    deliverables: ['DaVinci Resolve Color Grading', 'Professional Sound Design & Mix', 'Motion Graphics & VFX', 'Platform-Optimized Exports', 'Creative Direction Consulting'],
    img: 'https://readdy.ai/api/search-image?query=professional%20post%20production%20editing%20suite%20dark%20room%20large%20curved%20monitors%20DaVinci%20Resolve%20color%20grading%20software%20vibrant%20screen%20glow%20deep%20blacks%20neon%20ambient%20light%20premium%20workstation%20high%20end%20cinematic%20post%20production&width=1600&height=800&seq=svp3_06&orientation=landscape',
    keyword: 'Post Production Editing',
  },
];

const serviceQAs = [
  { q: 'What production services does Immanuel Production Group offer?', a: 'Immanuel Production Group offers six core broadcast-grade production services in Central Florida: corporate video production, brand story films, live streaming services, event coverage, photography, and creative direction & editing. Each service is executed at commercial and broadcast television quality standards with 20+ years of experience.' },
  { q: 'Who are these broadcast-grade production services designed for?', a: 'Their corporate video production and broadcast services are designed for businesses, brands, nonprofits, government organizations, and broadcasters who need cinematic-quality visual content to build brand authority, drive engagement, and achieve measurable business results in competitive markets.' },
  { q: 'What are the benefits of working with a professional broadcast production company?', a: 'Professional broadcast-grade production ensures your brand is represented with cinematic-quality visuals, strategic storytelling, and precise technical execution. This builds credibility with audiences, engages viewers deeply, and creates content that performs across digital and broadcast channels with measurable impact.' },
];

function ServiceHero() {
  return (
    <section className="relative min-h-[80vh] flex items-end justify-start overflow-hidden bg-[#020202]">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20cinema%20anamorphic%20camera%20close%20up%20lens%20flare%20bokeh%20dark%20production%20set%20dramatic%20golden%20rim%20light%20moody%20atmospheric%20dark%20near%20black%20warm%20glow%20premium%20luxury%20filmmaking%20behind%20the%20scenes%20broadcast%20grade%20equipment&width=1920&height=1000&seq=svp3_hero&orientation=landscape"
          alt="Professional broadcast production services - Immanuel Production Group"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020202] via-[#020202]/55 to-[#020202]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020202]/85 to-transparent" />
        <div className="absolute inset-0 bg-grain opacity-25 pointer-events-none" />
      </div>
      <div className="relative z-10 max-w-7xl w-full mx-auto px-8 lg:px-20 pb-24 pt-52">
        <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">What We Offer</p>
        <h1 className="font-serif font-bold text-white leading-[0.95] mb-6" style={{ fontSize: 'clamp(3.2rem, 7vw, 7rem)', letterSpacing: '-0.03em' }}>
          Broadcast Production<br />
          Services Built for{' '}
          <em className="not-italic text-gold">Impact</em>
        </h1>
        <p className="font-sans text-lg text-white/50 font-light leading-relaxed max-w-xl">
          Six broadcast-grade production disciplines. 20+ years of Central Florida excellence. One commitment — making your brand impossible to ignore.
        </p>
        <div className="flex items-center gap-4 mt-8">
          <div className="w-10 h-px bg-gold" />
          <span className="font-sans text-xs text-white/35 tracking-wide">Serving Oviedo, Orlando, Tampa &amp; Florida-wide</span>
        </div>
      </div>
    </section>
  );
}

interface ServiceBlockProps {
  svc: typeof services[0];
  index: number;
}

function ServiceBlock({ svc, index }: ServiceBlockProps) {
  const { ref, inView } = useInView();
  return (
    <article
      ref={ref as React.RefObject<HTMLDivElement>}
      id={svc.id}
      className={`transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
    >
      {/* Full-width image */}
      <div className="relative w-full overflow-hidden" style={{ height: '360px' }}>
        <img src={svc.img} alt={svc.title} className="w-full h-full object-cover object-center hover:scale-[1.03] transition-transform duration-[2.5s] ease-out" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/15 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0c0c0c]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 flex items-end justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-[#0c0c0c]/80 backdrop-blur-sm border border-gold/30">
              <i className={`${svc.icon} text-gold text-base`} />
            </div>
            <span className="font-sans text-[0.58rem] tracking-[0.28em] uppercase text-gold/80 bg-[#0c0c0c]/70 backdrop-blur-sm px-3 py-1.5">{svc.keyword}</span>
          </div>
          <span className="font-serif font-bold text-white/4 leading-none" style={{ fontSize: '6rem' }}>{String(index + 1).padStart(2, '0')}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-10 lg:p-14 xl:p-16 bg-[#0c0c0c] border-t border-white/5">
        <h2 className="font-serif text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3 leading-tight">{svc.title}</h2>
        <p className="font-sans text-sm text-gold/70 italic mb-5">{svc.headline}</p>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-px bg-gold" />
          <div className="w-3 h-px bg-gold/40" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <p className="font-sans text-sm text-white/48 leading-[2]">{svc.desc}</p>
          <div>
            <p className="font-sans text-[0.58rem] tracking-[0.3em] uppercase text-gold/55 mb-5 font-medium">Deliverables</p>
            <ul className="space-y-3">
              {svc.deliverables.map((d) => (
                <li key={d} className="flex items-center gap-3 font-sans text-sm text-white/55">
                  <div className="w-1.5 h-1.5 bg-gold flex-shrink-0" />
                  {d}
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              className="whitespace-nowrap mt-8 w-fit inline-flex items-center gap-3 font-sans text-[0.65rem] tracking-[0.22em] uppercase px-8 py-4 bg-gold/8 border border-gold/40 text-gold hover:bg-gold hover:text-[#020202] transition-all duration-300 cursor-pointer group"
            >
              Get a Quote
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ServicesPage() {
  return (
    <main>
      <ServiceHero />
      <TrustBar />
      <section className="bg-[#0c0c0c]">
        <div className="max-w-7xl mx-auto px-8 lg:px-20 py-16">
          <div className="flex flex-col gap-0">
            {services.map((svc, i) => (
              <div key={svc.id} className={i < services.length - 1 ? 'border-b border-white/5' : ''}>
                <ServiceBlock svc={svc} index={i} />
              </div>
            ))}
          </div>
        </div>
      </section>
      <MidPageCTA />
      <AIAnswerBlock heading="About Our Broadcast Production Services" qas={serviceQAs} />
      <ServiceAreas />
      <FAQ />
      <FinalCTA
        title="Ready to Elevate Your Production?"
        subtitle="Tell us your vision — we'll build the strategy and execute with cinematic precision."
      />
    </main>
  );
}
